import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import ReactMarkdown from "react-markdown";
import { Megaphone, X } from "lucide-react";

/**
 * Renders all currently-active broadcasts for a site as banners.
 * Driven by RPC lbs_active_broadcasts_for_site (migration 057).
 * Used on the public Site Info page (and anywhere else useful).
 */
export default function SiteBroadcastBanners({ siteId }) {
  const [rows, setRows] = useState([]);
  const [dismissed, setDismissed] = useState(() => readDismissed());

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("lbs_active_broadcasts_for_site", { p_site_id: siteId });
      if (!cancelled) setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  const visible = rows.filter((r) => !dismissed.includes(r.id));
  if (visible.length === 0) return null;

  const close = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try { localStorage.setItem("lbs.broadcastsDismissed", JSON.stringify(next)); } catch (_e) { /* storage unavailable */ }
  };

  return (
    <div className="space-y-2" data-testid="site-broadcast-banners">
      {visible.map((b) => (
        <div key={b.id} className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex gap-3"
             data-testid={`broadcast-banner-${b.id}`}>
          <Megaphone className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">{b.title}</div>
            <div className="prose prose-sm max-w-none text-sm prose-p:my-0 text-amber-900/90 mt-0.5">
              <ReactMarkdown>{b.body_markdown || ""}</ReactMarkdown>
            </div>
            {b.banner_expires_at && (
              <div className="text-[11px] text-amber-700 mt-1">
                Active until {new Date(b.banner_expires_at).toLocaleString("en-AU", { hourCycle: "h23" })}
              </div>
            )}
          </div>
          <button onClick={() => close(b.id)}
                  className="text-amber-700/70 hover:text-amber-900"
                  data-testid={`broadcast-dismiss-${b.id}`} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function readDismissed() {
  try { return JSON.parse(localStorage.getItem("lbs.broadcastsDismissed") || "[]"); }
  catch (_e) { return []; }
}
